import React, { useEffect, useId, useRef, useState } from 'react';
import { meterView, meterRows } from '../../services/meterFormat';

// The meter under the composer (founder, 2026-10-30). It was "51% of this
// month · resets Nov 1" beside the model's name on every turn, which reads as
// a countdown even when there is plenty left. Now a thin bar that says
// "Plenty left" until the five-hour window or the week passes 70%, and then
// says which one and when it is back ("This window 82% · resets 3:10 pm").
// Pressing it lists all three, window, week and month, with when each starts
// again. Percentages only, never dollars (docs/plans/chat-2.md "The meter").
// The meter comes from GET /api/plans/usage on load and from each turn's
// `usage` event (OmniAgentContext).
export default function UsageMeter({ meter, timeZone }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const popId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const view = meterView(meter, { timeZone });
  if (!view) return null;
  // The popover's lines, worked out only while it is open.
  const rows = open ? meterRows(meter, { timeZone }) : [];

  const onKeyDown = (e) => {
    if (open && e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus({ preventScroll: true });
    }
  };

  return (
    <div className={`um${view.warn ? ' is-warn' : ''}${open ? ' is-open' : ''}`} ref={wrapRef} onKeyDown={onKeyDown}>
      <button
        ref={buttonRef}
        type="button"
        className="um-trigger"
        aria-expanded={open}
        aria-controls={popId}
        aria-label={`Usage: ${view.label}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className="um-bar" aria-hidden="true"><span className="um-fill" style={{ width: `${view.pct}%` }} /></span>
        <span className="um-label" aria-hidden="true">{view.label}</span>
      </button>
      {open && (
        <div className="um-pop" id={popId} role="region" aria-label="Your usage">
          <ul className="um-rows">
            {rows.map(r => (
              <li key={r.key} className="um-row">
                <span className="um-name">{r.name}</span>
                <span className="um-pct">{r.pct}%</span>
                <span className="um-when">{r.resets}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
